import React, { useEffect, useState, useRef } from "react";
import { httpService } from "../../services/httpService";
import { HiArrowSmLeft, HiCheckCircle, HiExclamationCircle } from "react-icons/hi";

// ─── Notification ──────────────────────────────────────────────────────────
const Notification = ({ message, type, onClose }) => {
  if (!message) return null;

  const icon =
    type === "success" ? (
      <HiCheckCircle className="w-6 h-6 text-green-500" />
    ) : (
      <HiExclamationCircle className="w-6 h-6 text-red-500" />
    );
  const textColor = type === "success" ? "text-green-700" : "text-red-700";
  const bgColor = type === "success" ? "bg-green-100" : "bg-red-100";
  const borderColor = type === "success" ? "border-green-400" : "border-red-400";

  return (
    <div
      className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 border ${bgColor} ${borderColor} transition-transform transform translate-x-0 animate-fade-in`}
    >
      {icon}
      <span className={`font-semibold ${textColor}`}>{message}</span>
      <button
        onClick={onClose}
        className="text-gray-500 hover:text-gray-800 transition-colors"
      >
        &times;
      </button>
    </div>
  );
};

// ─── Helpers ───────────────────────────────────────────────────────────────
const normalizeScan = (value) =>
  String(value || "")
    .replace(/[^0-9a-z]/gi, "")
    .toLowerCase();

// ─── Main Component ────────────────────────────────────────────────────────
const SupplierReturn = ({ open, onClose, supplierId, supplierName }) => {
  const [supplier, setSupplier] = useState(null);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [saving, setSaving] = useState(false);

  const [notification, setNotification] = useState({ message: "", type: "" });

  const scanInputRef = useRef(null);

  // ── Clear when closing ───────────────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      setProducts([]);
      setLoadingProducts(false);
      setSupplier(null);
      setNotification({ message: "", type: "" });
    }
  }, [open]);

  // ── Load products on open ────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !supplierId) return;

    const fetchProducts = async () => {
      setLoadingProducts(true);
      setProducts([]);
      try {
        const res = await httpService.get(
          `/vendorsupplier/getVendorSupplierProducts/${supplierId}`
        );

        if (res?.status && res?.code === 200 && res?.data) {
          setSupplier({
            name: res.data.supplier_name,
            id: res.data.supplier_id,
          });

          const productsArray = Array.isArray(res.data.products)
            ? res.data.products
            : [];

          const mappedProducts = productsArray.map((item, index) => {
            const uniqueKey = `${item._id}_${item.purchase_price}_${item.available_qty}_${item.sku_number}_${index}`;
            return {
              id: item._id,
              uniqueKey,
              productName: item.product_name || "N/A",
              sku: item.sku_number || "-",
              barcode: item.barcode || null,
              availableQty: Number(item.available_qty) || 0,
              returnQty: 0,
              purchasePrice: Number(item.purchase_price) || 0,
              gst: Number(item.gst_slab) || 0,
              totalPrice: 0,
              mrp: Number(item.mrp_price) || 0,
            };
          });
          setProducts(mappedProducts);
        }
      } catch (error) {
        console.error("Error loading products:", error);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, [open, supplierId, supplierName]);

  // ── Auto-hide notification ───────────────────────────────────────────────
  useEffect(() => {
    if (notification.message) {
      const timer = setTimeout(() => {
        setNotification({ message: "", type: "" });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // ── Scan handler ─────────────────────────────────────────────────────────
  const handleScanKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const raw = e.target.value.trim();
    if (!raw) return;

    const q = normalizeScan(raw);

    // Match on SKU first, then barcode
    const match = products.find((p) => {
      const skuKey = normalizeScan(p.sku);
      const barcodeKey = normalizeScan(p.barcode);
      return skuKey === q || barcodeKey === q;
    });

    if (!match) {
      setNotification({
        message: `"${raw}" was not purchased from this supplier`,
        type: "error",
      });
      if (scanInputRef.current) {
        scanInputRef.current.value = "";
        scanInputRef.current.focus();
      }
      return;
    }

    // ── Increment return qty by 1, clamped to availableQty ────────────────
    const currentQty = Number(match.returnQty) || 0;
    const nextQty = currentQty + 1;

    if (nextQty > match.availableQty) {
      setNotification({
        message: `Cannot return more than ${match.availableQty} of ${match.productName}.`,
        type: "error",
      });
      focusRow(match.uniqueKey);
      if (scanInputRef.current) {
        scanInputRef.current.value = "";
        scanInputRef.current.focus();
      }
      return;
    }

    // Recompute total for this row
    const totalPrice = (
      nextQty *
      match.purchasePrice *
      (1 + match.gst / 100)
    ).toFixed(2);

    setProducts((prev) =>
      prev.map((p) =>
        p.uniqueKey === match.uniqueKey
          ? {
              ...p,
              returnQty: nextQty,
              totalPrice: parseFloat(totalPrice),
            }
          : p
      )
    );

    // Visual feedback
    focusRow(match.uniqueKey);
    setNotification({
      message: `${match.productName} × ${nextQty}`,
      type: "success",
    });

    if (scanInputRef.current) {
      scanInputRef.current.value = "";
      scanInputRef.current.focus();
    }
  };

  // ── Helper: scroll to row, flash, focus qty input ────────────────────────
  const focusRow = (uniqueKey) => {
    const rowEl = document.querySelector(
      `[data-scan-row="${CSS.escape(uniqueKey)}"]`
    );
    if (!rowEl) return;
    rowEl.scrollIntoView({ behavior: "smooth", block: "center" });
    rowEl.classList.add("ring-2", "ring-blue-500", "bg-blue-50");
    setTimeout(() => {
      rowEl.classList.remove("ring-2", "ring-blue-500", "bg-blue-50");
    }, 1500);
  };

  // ── Return qty change ────────────────────────────────────────────────────
  const handleReturnQtyChange = (uniqueKey, value) => {
    const numericValue = parseInt(value, 10) || 0;
    const product = products.find((p) => p.uniqueKey === uniqueKey);
    if (!product) return;

    if (numericValue > product.availableQty) {
      setNotification({
        message: `Cannot return more than ${product.availableQty} items.`,
        type: "error",
      });
      return;
    }

    if (notification.message && numericValue <= product.availableQty) {
      setNotification({ message: "", type: "" });
    }

    const newProducts = products.map((p) => {
      if (p.uniqueKey === uniqueKey) {
        const newReturnQty = numericValue < 0 ? 0 : numericValue;
        const totalPrice = (
          newReturnQty *
          p.purchasePrice *
          (1 + p.gst / 100)
        ).toFixed(2);
        return {
          ...p,
          returnQty: newReturnQty,
          totalPrice: parseFloat(totalPrice),
        };
      }
      return p;
    });
    setProducts(newProducts);
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const items = products.filter((item) => item.returnQty > 0);

    if (items.length === 0) {
      setNotification({
        message: "Please enter at least one return quantity.",
        type: "error",
      });
      return;
    }

    const hasInvalidQty = items.some((p) => p.returnQty > p.availableQty);
    if (hasInvalidQty) {
      setNotification({
        message: "Please correct the invalid quantities before saving.",
        type: "error",
      });
      return;
    }

    const hasNegativeQty = items.some((p) => p.availableQty < 0);
    if (hasNegativeQty) {
      setNotification({
        message: "Cannot return products with negative available quantity.",
        type: "error",
      });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        supplier_id: supplierId,
        items: items.map((item) => ({
          vendor_product_id: item.id,
          sku_number: item.sku,
          return_quantity: item.returnQty,
          purchase_price: item.purchasePrice,
          gst_amount: item.gst,
          return_amount: item.totalPrice,
          mrp: item.mrp,
        })),
      };

      const response = await httpService.post(
        "/vendorsupplierreturn/addVendorSupplierReturn",
        payload
      );

      if (response?.status) {
        setNotification({
          message: "Returns saved successfully!",
          type: "success",
        });
        // Reset return quantities locally, don't refetch (avoids flicker)
        setProducts((prev) =>
          prev.map((p) => ({ ...p, returnQty: 0, totalPrice: 0 }))
        );
      } else {
        setNotification({
          message:
            response?.message || "Failed to save returns. Please try again.",
          type: "error",
        });
      }
    } catch (err) {
      console.error("Error saving returns:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to save returns. Please try again.";
      setNotification({ message: errorMessage, type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    "Product Name",
    "SKU",
    "Available Qty",
    "Return Qty",
    "Purchase Price",
    "GST",
    "Total Price",
  ];

  const totalReturnAmount = products.reduce(
    (sum, row) => sum + (row.totalPrice || 0),
    0
  );

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-end bg-black bg-opacity-40">
        <div className="bg-white w-full max-w-4xl h-full shadow-xl p-6 animate-slide-in-right overflow-y-auto overflow-x-hidden relative flex flex-col">
          <div className="flex justify-start items-center border-b bg-white shadow-sm gap-2 mb-5 w-full">
            <button
              type="button"
              onClick={onClose}
              className="flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            >
              <HiArrowSmLeft className="w-8 h-8 mb-4" />
            </button>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">
              Supplier Returns
            </h2>
          </div>

          <div className="w-full flex-1 pb-24">
            <div className="mb-6 text-gray-600 text-base">
              Supplier Name: {supplier?.name || supplierName || "N/A"}
            </div>
            <div className="mb-6 text-gray-600 text-base">
              Return Date: {new Date().toLocaleDateString("en-GB")}
            </div>

            {/* ── Scan input ──────────────────────────────────────────── */}
            <div className="mb-4">
              <input
                ref={scanInputRef}
                type="text"
                onKeyDown={handleScanKeyDown}
                placeholder="Scan barcode / SKU to find a product in this return list…"
                autoComplete="off"
                autoFocus
                className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">
                Scan the product label — the matching row will be highlighted.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full table-auto border-separate border-spacing-y-2 text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    {columns.map((col) => (
                      <th
                        key={col}
                        className="px-4 py-2 text-left font-semibold text-gray-600 whitespace-nowrap"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loadingProducts ? (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="text-center py-6 text-gray-400"
                      >
                        Loading…
                      </td>
                    </tr>
                  ) : products.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="text-center py-6 text-gray-400"
                      >
                        No return records found.
                      </td>
                    </tr>
                  ) : (
                    products.map((row) => (
                      <tr
                        key={row.uniqueKey}
                        data-scan-row={row.uniqueKey}
                        className="bg-white shadow rounded border border-gray-100 transition-all duration-300"
                      >
                        <td className="px-4 py-2 text-left whitespace-nowrap">
                          {row.productName}
                        </td>
                        <td className="px-4 py-2 text-left whitespace-nowrap">
                          {row.sku}
                        </td>
                        <td className="px-4 py-2 text-left whitespace-nowrap">
                          {row.availableQty}
                        </td>
                        <td className="px-4 py-2 text-left whitespace-nowrap">
                          <input
                            type="number"
                            min="0"
                            value={row.returnQty}
                            onChange={(e) =>
                              handleReturnQtyChange(row.uniqueKey, e.target.value)
                            }
                            className="w-20 border rounded px-2 py-1 focus:outline-none focus:ring-2 border-gray-300 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-2 text-left whitespace-nowrap">
                          ₹{row.purchasePrice.toFixed(2)}
                        </td>
                        <td className="px-4 py-2 text-left whitespace-nowrap">
                          {row.gst}%
                        </td>
                        <td className="px-4 py-2 text-left whitespace-nowrap">
                          ₹{row.totalPrice.toFixed(2)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-base">
              <div className="flex flex-col gap-1 text-slate-700">
                <div>
                  Total Return Amount:{" "}
                  <span className="font-bold">
                    ₹{totalReturnAmount.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="fixed bottom-0 right-0 w-full max-w-4xl bg-white border-t border-gray-200 p-4 flex justify-end gap-3 z-50">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-yellow-600 border border-yellow-600 rounded-md bg-white hover:bg-yellow-50 font-semibold"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-6 py-2 bg-green-600 text-white rounded-md font-semibold hover:bg-green-700 transition-colors disabled:opacity-50"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>

      <Notification
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: "", type: "" })}
      />
    </>
  );
};

export default SupplierReturn;